import React from 'react';
import { Box, SelectChangeEvent, Button, Typography } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { ClientData } from '../../../core/api/clients/ClientModel';
import ClientStateSelectInput from '../../classifier/ClientStateSelectInput';
import ManagersSelectInput from '../../classifier/ManagersSelectInput';
import { DatePicker } from '../../common/DatePicker';
import { translations } from '../../../translations/common';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface ClientDetailsSectionProps {
  values: Partial<ClientData>;
  errors: FormikErrors<ClientData>;
  touched: FormikTouched<ClientData>;
  handleChange: (e: React.ChangeEvent<any> | SelectChangeEvent<string>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  className: string;
  textFieldClassName: string;
}

export const ClientDetailsSection: React.FC<ClientDetailsSectionProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  className,
  textFieldClassName,
}) => {
  const handleCompanyLookup = () => {
    if (values.companyCode) {
      const url = `https://rekvizitai.vz.lt/imones/1/?scrollTo=searchForm&name=&company_code=${values.companyCode}&search_word=&industry=&search_terms=&location=&catUrlKey=&resetFilter=0&order=1&redirected=1`;
      window.open(url, '_blank');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '12px',
        '& .MuiInputBase-root': { width: '100%' },
        '& .MuiOutlinedInput-root': { width: '100%' },
        '& .MuiFormControl-root': { width: '100%' }
      }}
    >
      <ClientStateSelectInput
        className={className}
        name="clientState"
        labelId="user-responsible-label"
        id="user-type-select"
        value={values.clientState}
        label="Kliento statusas"
        error={Boolean(errors.clientState)}
        fullWidth
        onChange={handleChange}
        onBlur={handleBlur}
        errors={errors}
        touched={touched}
        variant="outlined"
      />

      <Box sx={{ width: '100%' }}>
        <DatePicker
          id="validFrom"
          name="validFrom"
          label={translations.datePickerLabels.periodFrom}
          value={values.validFrom ?? null}
          onChange={handleChange}
          error={!!errors.validFrom}
          helperText={errors.validFrom}
          fullWidth
          className={className}
        />
      </Box>

      <Box sx={{ width: '100%' }}>
        <DatePicker
          id="validTo"
          name="validTo"
          label={translations.datePickerLabels.periodTo}
          value={values.validTo ?? null}
          onChange={handleChange}
          error={!!errors.validTo}
          helperText={errors.validTo}
          fullWidth
          className={className}
        />
      </Box>

      <ManagersSelectInput
        className={className}
        name="responsibleUser"
        labelId="user-responsible-label"
        id="user-responsible-select"
        value={values.responsibleUser}
        label="Atsakingas asmuo"
        fullWidth
        onChange={handleChange}
        onBlur={handleBlur}
        variant="outlined"
      />

      <Box sx={{ width: '100%', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={handleCompanyLookup}
          disabled={!values.companyCode}
          fullWidth
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            py: 1.5,
            backgroundColor: 'grey.100',
            borderColor: 'grey.300',
            color: 'grey.700',
            '&:hover': {
              backgroundColor: 'grey.200',
              borderColor: 'grey.400'
            },
            '&:disabled': {
              backgroundColor: 'grey.50',
              borderColor: 'grey.200',
              color: 'grey.400'
            }
          }}
        >
          {values.companyCode
            ? `Peržiūrėti įmonės duomenis`
            : 'Įveskite įmonės kodą'
          }
        </Button>
      </Box>
    </Box>
  );
}; 